import { useCallback, useEffect, useState } from "react";
import {
  ThemeProviderContext,
  type Theme,
  type ThemeProviderProps,
} from "@/lib/hooks/useTheme";

export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "arizona-ui-theme",
  ...props
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored === "light" || stored === "dark" || stored === "system") {
        return stored;
      }
    } catch {
      // A böngésző biztonsági beállításai tilthatják a localStorage-t
    }
    return defaultTheme;
  });

  const [resolvedTheme, setResolvedTheme] = useState<"dark" | "light">(() => {
    if (typeof window === "undefined") return "light";
    if (theme === "dark") return "dark";
    if (theme === "light") return "light";
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });

  useEffect(() => {
    const root = window.document.documentElement;
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const applyTheme = () => {
      const isSystemDark = mediaQuery.matches;
      const effectiveTheme = theme === "system" ? (isSystemDark ? "dark" : "light") : theme;

      root.classList.remove("light", "dark");
      root.classList.add(effectiveTheme);
      root.style.colorScheme = effectiveTheme;
      setResolvedTheme(effectiveTheme);
    };

    applyTheme();

    if (theme === "system") {
      mediaQuery.addEventListener("change", applyTheme);
      return () => mediaQuery.removeEventListener("change", applyTheme);
    }
  }, [theme]);

  const setTheme = useCallback(
    (newTheme: Theme) => {
      try {
        localStorage.setItem(storageKey, newTheme);
      } catch {
        // A böngésző biztonsági beállításai tilthatják a localStorage-t
      }
      setThemeState(newTheme);
    },
    [storageKey],
  );

  return (
    <ThemeProviderContext.Provider value={{ theme, resolvedTheme, setTheme }} {...props}>
      {children}
    </ThemeProviderContext.Provider>
  );
}
